/**
 * WhatsApp replies — slice 64. What a person sent back, read out of Meta's
 * webhook, for the funnel's Responded stage.
 *
 * ## THE SAME FIELD AS STATUSES, A DIFFERENT ARRAY
 *
 * Meta delivers a customer's message down the same `messages` change field as
 * the statuses of the messages Wend sent: `value.statuses[]` for those,
 * `value.messages[]` for theirs. Until this slice the router read only the
 * first, and an inbound message was stored raw in `webhook_events` and never
 * looked at again. {@link extractMetaInboundMessages} reads the second.
 *
 * ## LENIENT, ONE ELEMENT AT A TIME
 *
 * As `extractMetaMessageStatuses` does: each element parsed on its own, so one
 * unreadable message does not take its siblings with it, and the verbatim bytes
 * are already in `webhook_events` for a human.
 *
 * ## WHAT IS KEPT
 *
 *   - the sender's number, as E.164 (`+` and Meta's digits);
 *   - the wamid, which makes a redelivery a no-op;
 *   - `context.id`, the wamid of OUR message they replied to, when they used
 *     reply — the strongest tie between a reply and a send;
 *   - for a quick-reply button or an interactive reply, the PAYLOAD, which can
 *     be mapped to a conversion type. Wend's own templates set no payload on
 *     their quick-reply buttons (only `text`, and the send path fills only URL
 *     button parameters), and for such a button Meta returns the button's
 *     label as `button.payload` — so for Wend's sends the payload IS the
 *     button's text, and `button.text` is the fallback when the field is
 *     absent. Matching is exact, case included;
 *   - the text of a text message, capped — needed to read intent, shown only to
 *     the workspace, never logged (decision 7 of the programme);
 *   - since slice 70, a click-to-WhatsApp ad's `referral` (which ad, its
 *     headline, its link) and the sender's WhatsApp profile name from
 *     `value.contacts[]` — what makes a first message from an ad a Reach lead
 *     (`modules/meta-ads/reach-leads.service.ts`). Both are OPTIONAL on
 *     {@link MetaInboundMessage}, so every caller that builds one by hand (the
 *     backfill, the verify suites) still compiles and simply carries none.
 */
import { z } from 'zod';

import { META_MESSAGE_STATUS_FIELD } from './whatsapp-delivery.js';

export const WA_REPLY_KIND_VALUES = ['text', 'button', 'interactive', 'other'] as const;
export type WaReplyKind = (typeof WA_REPLY_KIND_VALUES)[number];

/** Longest text kept from one reply. WhatsApp allows 4,096. */
export const WA_REPLY_TEXT_MAX = 4096;
/** Longest quick-reply payload kept. Meta allows 256 for a button payload. */
export const WA_REPLY_PAYLOAD_MAX = 256;

export interface MetaInboundMessage {
  readonly phoneNumberId: string | null;
  /** The reply's own wamid. Null when Meta sent none — such a reply cannot be stored. */
  readonly messageId: string | null;
  /** E.164, or null when the sender's number is unreadable. */
  readonly from: string | null;
  readonly kind: WaReplyKind;
  /** Text messages only, capped. */
  readonly text: string | null;
  /** A button's payload or an interactive reply's id, capped. */
  readonly payload: string | null;
  /** The wamid of the message this replies to, when the person used reply. */
  readonly contextId: string | null;
  /** When the person sent it, or null when Meta's timestamp is unreadable. */
  readonly sentAt: Date | null;
  /** Slice 70: the ad a click-to-WhatsApp message came from, when Meta says so. */
  readonly referral?: MetaInboundReferral | null;
  /** Slice 70: the sender's WhatsApp profile name, capped. Never logged. */
  readonly profileName?: string | null;
}

/**
 * Meta's `messages[].referral` — present on the first message a person sends
 * from a click-to-WhatsApp ad (or a post). `sourceType` is Meta's own word
 * (`ad` | `post`); only `ad` makes a Reach lead. `sourceId` is the ad id.
 */
export interface MetaInboundReferral {
  readonly sourceType: string | null;
  readonly sourceId: string | null;
  readonly sourceUrl: string | null;
  readonly headline: string | null;
}

/** Longest referral field kept — an ad id is ~18 digits, a headline is short. */
export const WA_REFERRAL_FIELD_MAX = 500;

const inboundElementSchema = z
  .object({
    id: z.string().optional(),
    from: z.union([z.string(), z.number()]).optional(),
    timestamp: z.union([z.string(), z.number()]).optional(),
    type: z.string().optional(),
    text: z.object({ body: z.string().optional() }).passthrough().optional(),
    button: z.object({ payload: z.string().optional(), text: z.string().optional() }).passthrough().optional(),
    interactive: z
      .object({
        type: z.string().optional(),
        button_reply: z.object({ id: z.string().optional(), title: z.string().optional() }).passthrough().optional(),
        list_reply: z.object({ id: z.string().optional(), title: z.string().optional() }).passthrough().optional(),
      })
      .passthrough()
      .optional(),
    context: z.object({ id: z.string().optional() }).passthrough().optional(),
    referral: z
      .object({
        source_type: z.string().optional(),
        source_id: z.union([z.string(), z.number()]).optional(),
        source_url: z.string().optional(),
        headline: z.string().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const inboundContactSchema = z
  .object({
    wa_id: z.union([z.string(), z.number()]).optional(),
    profile: z.object({ name: z.string().optional() }).passthrough().optional(),
  })
  .passthrough();

const inboundValueSchema = z
  .object({
    metadata: z.object({ phone_number_id: z.union([z.string(), z.number()]).optional() }).passthrough().optional(),
    messages: z.array(z.unknown()).optional(),
    contacts: z.array(z.unknown()).optional(),
  })
  .passthrough();

const nonEmpty = (value: string | undefined, max: number): string | null => {
  if (value === undefined) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed.slice(0, max);
};

function e164(from: string | number | undefined): string | null {
  if (from === undefined) return null;
  const digits = String(from).replace(/\D/g, '');
  // E.164 never starts with 0 — and 0046's shape CHECK refuses one, which
  // would throw inside the insert rather than read as "no readable sender".
  return /^[1-9][0-9]{6,14}$/.test(digits) ? `+${digits}` : null;
}

function sentAt(timestamp: string | number | undefined): Date | null {
  if (timestamp === undefined) return null;
  const seconds = Number(timestamp);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const date = new Date(seconds * 1000);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Every inbound customer message in a Meta webhook payload. */
export function extractMetaInboundMessages(payload: {
  readonly entry?: readonly { readonly changes?: readonly { readonly field?: string; readonly value?: unknown }[] }[];
}): MetaInboundMessage[] {
  const out: MetaInboundMessage[] = [];
  for (const entry of payload.entry ?? []) {
    for (const change of entry.changes ?? []) {
      if (change.field !== META_MESSAGE_STATUS_FIELD) continue;
      const value = inboundValueSchema.safeParse(change.value);
      if (!value.success) continue;
      const phoneNumberId =
        value.data.metadata?.phone_number_id === undefined ? null : String(value.data.metadata.phone_number_id);
      // Profile names by sender, read leniently like everything else here.
      const names = new Map<string, string>();
      for (const contact of value.data.contacts ?? []) {
        const parsedContact = inboundContactSchema.safeParse(contact);
        if (!parsedContact.success) continue;
        const waId = e164(parsedContact.data.wa_id);
        const name = nonEmpty(parsedContact.data.profile?.name, 200);
        if (waId !== null && name !== null) names.set(waId, name);
      }
      for (const element of value.data.messages ?? []) {
        const parsed = inboundElementSchema.safeParse(element);
        if (!parsed.success) continue;
        const message = parsed.data;
        const type = message.type ?? '';
        let kind: WaReplyKind = 'other';
        let payloadValue: string | null = null;
        if (type === 'text') kind = 'text';
        else if (type === 'button') {
          kind = 'button';
          payloadValue = nonEmpty(message.button?.payload ?? message.button?.text, WA_REPLY_PAYLOAD_MAX);
        } else if (type === 'interactive') {
          kind = 'interactive';
          payloadValue = nonEmpty(
            message.interactive?.button_reply?.id ?? message.interactive?.list_reply?.id,
            WA_REPLY_PAYLOAD_MAX,
          );
        }
        const from = e164(message.from);
        const referral =
          message.referral === undefined
            ? null
            : {
                sourceType: nonEmpty(message.referral.source_type, 32),
                sourceId:
                  message.referral.source_id === undefined
                    ? null
                    : nonEmpty(String(message.referral.source_id), WA_REFERRAL_FIELD_MAX),
                sourceUrl: nonEmpty(message.referral.source_url, WA_REFERRAL_FIELD_MAX),
                headline: nonEmpty(message.referral.headline, WA_REFERRAL_FIELD_MAX),
              };
        out.push({
          phoneNumberId,
          messageId: nonEmpty(message.id, 256),
          from,
          kind,
          text: kind === 'text' ? nonEmpty(message.text?.body, WA_REPLY_TEXT_MAX) : null,
          payload: payloadValue,
          contextId: nonEmpty(message.context?.id, 256),
          sentAt: sentAt(message.timestamp),
          referral,
          profileName: from === null ? null : (names.get(from) ?? null),
        });
      }
    }
  }
  return out;
}
